import { formatName } from '@/utils/name';
import { isValidEmail, isValidPassword } from '@/utils/validation';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { Box, CircularProgress, Dialog, DialogProps, IconButton, Stack, styled, Tooltip, tooltipClasses, TooltipProps, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import {
    DataGrid,
    GridActionsCellItem,
    GridCellParams,
    GridColDef,
    GridEditInputCell,
    GridEditSingleSelectCell,
    GridEditSingleSelectCellProps,
    GridEventListener,
    GridPreProcessEditCellProps,
    GridRenderEditCellParams,
    GridRowEditStopReasons,
    GridRowId,
    GridRowModel,
    GridRowModes,
    GridRowModesModel,
    useGridApiRef,
} from '@mui/x-data-grid';
import { randomId } from '@mui/x-data-grid-generator';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { ClassData, StudentData } from '../types/student';

interface StudentRowModel {
    id: string;
    familyName: string;
    givenName: string;
    class: string;
    studentNumber: string;
    email: string;
    name: string;
    password: string;
    isNew?: boolean;
}

const initialRow: StudentRowModel = {
    id: randomId(),
    familyName: '',
    givenName: '',
    class: '',
    studentNumber: '1',
    email: '',
    name: '',
    password: '',
    isNew: true,
};

const ROW_HEIGHT = 52;

function newRow(oldModel: StudentRowModel): StudentRowModel {
    const number = parseInt(oldModel.studentNumber);
    const studentNumber = String(number ? number + 1 : '');
    return { ...initialRow, class: oldModel.class, studentNumber: studentNumber, id: randomId(), isNew: true };
}

const StyledTooltip = styled(({ className, ...props }: TooltipProps) => <Tooltip {...props} classes={{ popper: className }} />)(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: (theme.vars || theme).palette.error.main,
        color: (theme.vars || theme).palette.error.contrastText,
    },
}));

function NameEditInputCell(props: GridRenderEditCellParams) {
    const { error } = props;

    return (
        <StyledTooltip
            title={error}
            arrow
            sx={{
                '& .MuiTooltip-arrow': {
                    color: 'error.main',
                },
            }}
            slotProps={{
                popper: {
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: [0, -6],
                            },
                        },
                    ],
                },
            }}
        >
            <GridEditInputCell {...props} error={!!error} />
        </StyledTooltip>
    );
}

function renderEditInput(params: GridRenderEditCellParams) {
    return <NameEditInputCell {...params} />;
}

function renderEmailInput(params: GridRenderEditCellParams) {
    return renderEditInput({ ...params, inputProps: { 'aria-label': 'email' } });
}

function renderPasswordInput(params: GridRenderEditCellParams) {
    return renderEditInput({ ...params, inputProps: { 'aria-label': 'password' } });
}

function renderFamilyNameInput(params: GridRenderEditCellParams) {
    return <GridEditInputCell {...params} inputProps={{ 'aria-label': 'familyName' }} />;
}

function renderGivenNameInput(params: GridRenderEditCellParams) {
    return <GridEditInputCell {...params} inputProps={{ 'aria-label': 'givenName' }} />;
}

function renderEditSelect(params: GridRenderEditCellParams, onValueChange?: GridEditSingleSelectCellProps['onValueChange']) {
    return <GridEditSingleSelectCell {...params} onValueChange={onValueChange} />;
}

export default function StudentBulkCreateDialog({
    open,
    payload,
    onClose,
}: {
    open: DialogProps['open'];
    payload?: { classes: ClassData[] };
    onClose: (data: (Pick<StudentData, 'familyName' | 'givenName' | 'name' | 'email' | 'studentNumber' | 'class'> & { password: string })[] | null) => Promise<void>;
}) {
    const { t } = useTranslation();
    const initialRows = React.useMemo<StudentRowModel[]>(() => [{ ...initialRow, class: payload?.classes[0]?.id || '' }], [payload?.classes]);
    const [rows, setRows] = React.useState(initialRows);
    const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>({ [initialRow.id]: { mode: GridRowModes.Edit, fieldToFocus: 'familyName' } });
    const [saving, setSaving] = React.useState(false);
    const apiRef = useGridApiRef();

    // remember last edit value in order to provide auto filled value on adding new row to provide better UX
    const [autoFilledRowValues, setAutoFilledRowValues] = React.useState<{ class: string; studentNumber: number }>({ class: payload?.classes[0]?.id || '', studentNumber: 1 });

    const handleRowEditStop = React.useCallback<GridEventListener<'rowEditStop'>>(
        async (params, event) => {
            if (!apiRef.current) {
                console.warn('API reference is not yet available');
                return;
            }

            if (params.reason === GridRowEditStopReasons.rowFocusOut) {
                event.defaultMuiPrevented = true;
            }

            const id = params.id;
            const updatedRowModel = apiRef.current.getRowWithUpdatedValues(id, '');
            if (!isValidEmail(updatedRowModel.email)) {
                await apiRef.current.setEditCellValue({ id, field: 'email', value: updatedRowModel.email });
            }

            if (!isValidPassword(updatedRowModel.password)) {
                await apiRef.current.setEditCellValue({ id, field: 'password', value: updatedRowModel.password });
            }

            setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
        },
        [apiRef, rowModesModel],
    );

    const handleDeleteClick = React.useCallback(
        (id: GridRowId) => () => {
            setRows((prev) => prev.filter((row) => row.id !== id));
        },
        [],
    );

    const processRowUpdate = React.useCallback((newRow: GridRowModel<StudentRowModel>) => {
        const updatedRow = { ...newRow, isNew: false };
        setRows((prevRows) => prevRows.map((row) => (row.id === newRow.id ? updatedRow : row)));
        return updatedRow;
    }, []);

    const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
        setRowModesModel(newRowModesModel);
    };

    const handleCellClick = React.useCallback((params: GridCellParams, event: React.MouseEvent) => {
        if (!params.isEditable) {
            return;
        }

        // Ignore portal
        if ((event.target as Node).nodeType === 1 && !event.currentTarget.contains(event.target as Element)) {
            return;
        }

        setRowModesModel((prevModel) => {
            return {
                ...prevModel,
                [params.id]: { mode: GridRowModes.Edit },
            };
        });
    }, []);

    const renderClassSelect = React.useCallback(
        (params: GridRenderEditCellParams<StudentRowModel, string>) =>
            renderEditSelect(params, async (_event, newValue) => {
                setAutoFilledRowValues((prev) => ({ ...prev, class: newValue }));
            }),
        [],
    );

    const columns = React.useMemo<GridColDef<StudentRowModel>[]>(
        () => [
            {
                field: 'familyName',
                headerName: t('student.label.familyName'),
                editable: true,
                disableColumnMenu: true,
                flex: 1,
                renderEditCell: renderFamilyNameInput,
            },
            {
                field: 'givenName',
                headerName: t('student.label.givenName'),
                editable: true,
                disableColumnMenu: true,
                flex: 1,
                renderEditCell: renderGivenNameInput,
            },
            {
                field: 'class',
                headerName: t('student.label.class'),
                editable: true,
                type: 'singleSelect',
                valueOptions: payload?.classes.map((c) => ({ label: c.name ? `${c.name}${c.schoolYear ? ` (${c.schoolYear})` : ''}` : '', value: c.id })) || [],
                disableColumnMenu: true,
                sortable: false,
                flex: 1.25,
                renderEditCell: renderClassSelect,
            },
            {
                field: 'studentNumber',
                headerName: t('student.label.number'),
                editable: true,
                disableColumnMenu: true,
                flex: 0.5,
            },
            {
                field: 'email',
                headerName: `${t('student.label.email')} *`,
                editable: true,
                disableColumnMenu: true,
                flex: 1,
                preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
                    const hasError = !isValidEmail(params.props.value);
                    return { ...params.props, error: hasError ? t('common.form.invalidEmail') : undefined };
                },
                renderEditCell: renderEmailInput,
            },
            {
                field: 'password',
                headerName: `${t('common.form.password')} *`,
                editable: true,
                disableColumnMenu: true,
                sortable: false,
                flex: 1,
                preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
                    const hasError = !isValidPassword(params.props.value);
                    return { ...params.props, error: hasError ? t('common.form.invalidPassword') : undefined };
                },
                renderEditCell: renderPasswordInput,
            },
            {
                field: 'actions',
                type: 'actions',
                headerName: t('common.actions'),
                cellClassName: 'actions',
                getActions: ({ id }) => {
                    return [<GridActionsCellItem icon={<DeleteIcon />} label='Delete' onClick={handleDeleteClick(id)} color='inherit' />];
                },
            },
        ],
        [handleDeleteClick, payload?.classes, renderClassSelect, t],
    );

    return (
        <Dialog
            open={open}
            onClose={(_, reason) => {
                if (reason === 'backdropClick') {
                    return;
                }
                onClose(null);
            }}
            fullWidth
            maxWidth='lg'
            sx={(theme) => ({
                '& .MuiDataGrid-cell--editable': {
                    backgroundColor: 'rgb(217 243 190)',
                    '& .MuiInputBase-root': {
                        height: '100%',
                    },
                    ...theme.applyStyles('dark', {
                        backgroundColor: '#376331',
                    }),
                },
                '& .Mui-error': {
                    backgroundColor: 'rgb(126,10,15, 0.1)',
                    color: '#750f0f',
                    ...theme.applyStyles('dark', {
                        backgroundColor: 'rgb(126,10,15, 0.4)',
                        color: '#ff4343',
                    }),
                },
            })}
        >
            <DataGrid
                rows={rows}
                columns={columns}
                editMode='row'
                rowModesModel={rowModesModel}
                onRowModesModelChange={handleRowModesModelChange}
                onRowEditStop={handleRowEditStop}
                processRowUpdate={processRowUpdate}
                onCellClick={handleCellClick}
                getRowHeight={() => 'auto'}
                disableRowSelectionOnClick
                sx={{
                    borderTop: 'none',
                    borderLeft: 'none',
                    borderRight: 'none',
                    // borderColor: 'divider',
                    borderRadius: 0,
                    '&.MuiDataGrid-root .MuiDataGrid-row--editing .MuiDataGrid-cell': {
                        backgroundColor: 'transparent',
                    },
                    '&.MuiDataGrid-root .MuiDataGrid-row--editing': {
                        boxShadow: 'none',
                    },
                    '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': {
                        py: '6px',
                        height: `${ROW_HEIGHT + 1}px`, // +1px for the border and DataGrid table will flicker if it is exactly 52px height (same as default row height) when adding a new row
                        alignContent: 'center',
                    },
                    '&.MuiDataGrid-root .MuiDataGrid-columnHeader': {
                        outline: 'none',
                    },
                    '--DataGrid-overlayHeight': '0px',
                }}
                slots={{
                    noRowsOverlay: () => <div style={{ border: '1px solid' }}></div>,
                    noResultsOverlay: () => <></>,
                    footer: () => (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                borderTop: rows.length > 0 ? '1px solid' : 'none',
                                borderColor: 'divider',
                                padding: 0.75,
                            }}
                        >
                            <IconButton
                                aria-label='add'
                                sx={{ height: 40, width: 40 }}
                                disabled={rows.length >= 100} // community plan can't disable pagniation and have a 100 max row limit
                                onClick={() => {
                                    const row = newRow({ ...initialRow, class: autoFilledRowValues.class, studentNumber: String(autoFilledRowValues.studentNumber) });
                                    setRows((oldRows) => [...oldRows, row]);
                                    setAutoFilledRowValues((prev) => ({ ...prev, studentNumber: prev.studentNumber + 1 }));
                                    setRowModesModel((oldModel) => ({
                                        ...oldModel,
                                        [row.id]: { mode: GridRowModes.Edit, fieldToFocus: 'familyName' },
                                    }));
                                }}
                            >
                                <AddCircleIcon />
                            </IconButton>
                        </Box>
                    ),
                }}
                apiRef={apiRef}
            />
            <Stack direction='row' sx={{ gap: 1, p: 1 }}>
                <Typography variant='caption' color='textSecondary'>
                    {t('student.caption.requiredField')}
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                <Button
                    onClick={async () => {
                        onClose(null);
                    }}
                >
                    {t('common.cancel')}
                </Button>
                <Button
                    loading={saving}
                    variant='contained'
                    aria-label='save'
                    type='submit'
                    onClick={async () => {
                        const api = apiRef.current;
                        if (!api) {
                            console.warn('API reference is not yet available');
                            return;
                        }

                        const updatedRowModels = rows.map((row) => api.getRowWithUpdatedValues(row.id, ''));
                        for (const model of updatedRowModels) {
                            if (!isValidEmail(model.email)) {
                                if (!model.email) {
                                    await api.setEditCellValue({ id: model.id, field: 'email', value: '' });
                                }
                                api.setCellFocus(model.id, 'email');
                                return;
                            }

                            if (!isValidPassword(model.password)) {
                                if (!model.password) {
                                    await api.setEditCellValue({ id: model.id, field: 'password', value: '' });
                                }
                                api.setCellFocus(model.id, 'password');
                                return;
                            }
                        }

                        setSaving(true);
                        await onClose(
                            updatedRowModels.map((row) => ({
                                familyName: row.familyName,
                                givenName: row.givenName,
                                name: formatName(row.familyName, row.givenName),
                                email: row.email,
                                studentNumber: row.studentNumber,
                                class: {
                                    id: row.class,
                                },
                                password: row.password,
                            })),
                        );
                        setSaving(false);
                    }}
                    loadingIndicator={<CircularProgress size={16} />}
                >
                    {t('common.save')}
                </Button>
            </Stack>
        </Dialog>
    );
}
